import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatCurrency } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import QueueToolbar from "./QueueToolbar";
import {
  Search, ChevronRight, ChevronUp, ChevronDown, AlertTriangle, Inbox, RefreshCw,
  Flame, Clock, CircleCheck,
} from "lucide-react";

/**
 * Partner work queue (participant-console-002, extended by -003). Consumes 001's
 * `GET /api/partner/console/queue`, whose response shape is
 * `{ total, count, limit, offset, rows: [partner_safe_view, ...] }`.
 *
 * Each row is the partner-safe projection — the ONLY field set the external
 * partner ever sees (services/partner_queue.partner_safe_view) — now enriched by
 * -003 with an independent `priority_tier` (P0/P1/P2) and an SLA clock
 * `{ due_by, breach, aging }`. The table renders the real projected fields plus
 * those computed fields; it never assumes a field outside that contract exists.
 *
 * Filter / search / sort / pagination all drive the API (server-side), matching
 * the query params 001 accepts (status, q, sort, order, limit, offset). The
 * default sort is `priority` — the backend's breached-first → tier → oldest
 * composite (-003). A row navigates to the nested `/console/:userId` detail
 * route (stub until 004).
 */

const PAGE_SIZE = 25;

// Partner-stage status labels (backend models/command_center.py). Used to
// populate the status filter; the backend rejects any non-partner-stage value
// with an empty queue, so this list mirrors the authoritative set.
const PARTNER_STAGE_STATUSES = [
  "Pending FinBPM Review",
  "FinBPM Approved",
  "FinBPM Rejected / More Info Needed",
  "Funding Instructions Sent",
  "Proof of Payment Uploaded",
  "Funds Confirmed",
  "Partner Account Created",
  "Dashboard Activated",
];

// Columns the partner can sort by. The backend `_SORTABLE` set
// (services/partner_queue.py) also permits `created_at` and the `priority`
// default, but the queue surfaces no created-at column, so this is an
// intentional subset. Sorting on any column outside this set is client-inert.
const SORTABLE = new Set(["full_name", "amount_elected", "updated_at"]);

// The backend's intelligence-aware default sort (participant-console-003):
// breached-first → tier (P0→P2) → oldest. The "Priority" column header is a
// sentinel that resets to this server-side composite.
const DEFAULT_SORT = "priority";

// Tier chip styling (impact-based, independent of the SLA clock). P0 = highest.
const TIER_CHIP = {
  P0: "bg-red-100 text-red-900 border-red-300",
  P1: "bg-orange-100 text-orange-900 border-orange-300",
  P2: "bg-slate-100 text-slate-600 border-slate-300",
};

// Aging badge styling + icon + label, keyed by the backend's `aging` value.
// `null` aging = informational status with no SLA clock → no badge.
const AGING_BADGE = {
  breached: { cls: "bg-red-100 text-red-900 border-red-300", Icon: Flame, label: "Breached" },
  "due-soon": { cls: "bg-amber-100 text-amber-900 border-amber-300", Icon: Clock, label: "Due soon" },
  "on-track": { cls: "bg-emerald-100 text-emerald-900 border-emerald-300", Icon: CircleCheck, label: "On track" },
};

const statusPill = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("rejected") || s.includes("more info")) return "bg-amber-100 text-amber-900 border-amber-300";
  if (s.includes("pending review")) return "bg-blue-100 text-blue-900 border-blue-300";
  if (s.includes("approved")) return "bg-emerald-100 text-emerald-900 border-emerald-300";
  if (s.includes("confirmed") || s.includes("activated") || s.includes("created")) return "bg-slate-100 text-slate-700 border-slate-300";
  return "bg-slate-100 text-slate-700 border-slate-300";
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

// Coarse "waiting since" relative age from the agreement's last update.
const waitingSince = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 48) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
};

// "Blocked on" — whose action the queue is waiting for, derived from the
// partner-safe status. Read-only inference; the authoritative SLA/aging model
// arrives in participant-console-003.
const blockedOn = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("pending review")) return "Partner";
  if (s.includes("more info") || s.includes("rejected")) return "Member";
  if (s.includes("funding instructions")) return "Member";
  if (s.includes("proof")) return "Partner";
  return "—";
};

// Impact tier chip (P0/P1/P2). Independent of the SLA clock per the -003 spine.
const TierChip = ({ tier }) => {
  if (!tier) return <span className="text-slate-400">—</span>;
  return (
    <span
      data-testid={`queue-tier-${tier}`}
      className={`px-1.5 py-0.5 rounded border text-xs font-semibold ${TIER_CHIP[tier] || TIER_CHIP.P2}`}
    >
      {tier}
    </span>
  );
};

// SLA aging badge (on-track / due-soon / breached). A row whose status has no
// SLA clock carries `aging === null` and renders a neutral dash.
const AgingBadge = ({ aging, dueBy }) => {
  if (!aging) return <span className="text-slate-400 text-xs">—</span>;
  const { cls, Icon, label } = AGING_BADGE[aging] || AGING_BADGE["on-track"];
  return (
    <span
      data-testid={`queue-aging-${aging}`}
      title={dueBy ? `Due by ${fmtDateTime(dueBy)}` : undefined}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-xs font-medium ${cls}`}
    >
      <Icon size={12} />
      {label}
    </span>
  );
};

const SortHeader = ({ label, field, sort, order, onSort, className = "" }) => {
  const active = sort === field;
  // The `priority` sentinel is clickable (resets to the default composite sort)
  // but carries no asc/desc chevron — its ordering is fixed server-side.
  const isPriority = field === DEFAULT_SORT;
  const sortable = SORTABLE.has(field) || isPriority;
  return (
    <th className={`text-left p-3 ${className}`}>
      {sortable ? (
        <button
          type="button"
          onClick={() => onSort(field)}
          data-testid={`queue-sort-${field}`}
          className={`inline-flex items-center gap-1 font-medium uppercase tracking-wide text-xs ${
            active ? "text-slate-900" : "text-slate-500 hover:text-slate-800"
          }`}
        >
          {label}
          {active && !isPriority ? (order === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />) : null}
        </button>
      ) : (
        <span className="font-medium uppercase tracking-wide text-xs text-slate-500">{label}</span>
      )}
    </th>
  );
};

const QueueTable = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [order, setOrder] = useState("desc");
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // participant-console-009: multi-selection for bulk-safe triage. A Set of
  // selected user_ids drives the row checkboxes + QueueToolbar's bulk bar.
  const [selected, setSelected] = useState(() => new Set());

  // Read the search box via a ref so typing doesn't re-fire the load effect on
  // every keystroke; the form submit / Refresh button is the apply trigger
  // (matches the AdminCommandCenterPage idiom).
  const searchRef = useRef(search);
  searchRef.current = search;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/partner/console/queue", {
        params: {
          status: status || undefined,
          q: searchRef.current || undefined,
          sort,
          order,
          limit: PAGE_SIZE,
          offset,
        },
      });
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load the work queue.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [status, sort, order, offset]);

  useEffect(() => { load(); }, [load]);

  const onSort = (field) => {
    if (field === DEFAULT_SORT) {
      // Reset to the server-side breached-first → tier → oldest composite.
      setSort(DEFAULT_SORT);
      setOrder("desc");
    } else if (sort === field) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(field);
      setOrder("desc");
    }
    setOffset(0);
  };

  const applySearch = (e) => {
    e.preventDefault();
    // If already on the first page, offset won't change so the load effect
    // won't re-fire — load explicitly. Otherwise just reset the offset and let
    // the effect issue the single request (avoids a duplicate call with the
    // stale offset).
    if (offset === 0) {
      load();
    } else {
      setOffset(0);
    }
  };

  const rows = data?.rows || [];
  const total = data?.total ?? 0;

  // ---- multi-selection (participant-console-009) ----
  const selectableIds = rows.map((r) => r.user_id).filter(Boolean);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const selectedIds = Array.from(selected);

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (selectableIds.every((id) => next.has(id))) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  // Apply a saved filter's persisted criteria back into the query state.
  const applySavedFilter = (criteria = {}) => {
    if ("status" in criteria) setStatus(criteria.status || "");
    if ("q" in criteria) setSearch(criteria.q || "");
    if ("sort" in criteria) setSort(criteria.sort || DEFAULT_SORT);
    if ("order" in criteria) setOrder(criteria.order || "desc");
    setOffset(0);
    clearSelection();
  };

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = offset + rows.length;
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div data-testid="partner-queue">
      {/* CONTROLS */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <form onSubmit={applySearch} className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input
            data-testid="queue-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, VC#..."
            className="pl-9 bg-white text-slate-900 border-slate-300"
          />
        </form>

        <select
          data-testid="queue-status-filter"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
          className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
        >
          <option value="">All statuses</option>
          {PARTNER_STAGE_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <Button
          type="button"
          onClick={load}
          variant="outline"
          className="border-slate-300 text-slate-700"
          data-testid="queue-refresh"
        >
          <RefreshCw size={14} className="mr-1.5" />
          Refresh
        </Button>
      </div>

      {/* WORKSPACE TOOLBAR (participant-console-009): saved-filter switcher +
          bulk-safe triage bar for the current multi-selection. */}
      <QueueToolbar
        selectedIds={selectedIds}
        currentCriteria={{ status, q: search, sort, order }}
        onApplyFilter={applySavedFilter}
        onClearSelection={clearSelection}
        onTriageDone={load}
      />

      {/* TABLE */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="py-3">
          <CardTitle className="text-slate-900 text-base">
            Work Queue · {total} participant{total === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="queue-loading">
              Loading the work queue...
            </div>
          ) : error ? (
            <div className="p-8 text-center" data-testid="queue-error">
              <AlertTriangle className="mx-auto text-amber-500 mb-2" size={22} />
              <p className="text-sm text-slate-700">{error}</p>
              <Button
                size="sm"
                variant="outline"
                onClick={load}
                className="mt-3 border-slate-300 text-slate-700"
              >
                Try again
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center" data-testid="queue-empty">
              <Inbox className="mx-auto text-slate-300 mb-2" size={26} />
              <p className="text-sm text-slate-600">No participants awaiting your review.</p>
              {(status || search) && (
                <p className="text-xs text-slate-400 mt-1">Try clearing the filter or search.</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="p-3 w-8">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        data-testid="queue-select-all"
                        aria-label="Select all rows on this page"
                        className="cursor-pointer"
                      />
                    </th>
                    <SortHeader label="Priority" field="priority" sort={sort} order={order} onSort={onSort} />
                    <SortHeader label="Participant" field="full_name" sort={sort} order={order} onSort={onSort} />
                    <SortHeader label="VC#" field="vc_number" sort={sort} order={order} onSort={onSort} />
                    <SortHeader label="Agreement status" field="agreement_status" sort={sort} order={order} onSort={onSort} />
                    <SortHeader label="Amount" field="amount_elected" sort={sort} order={order} onSort={onSort} />
                    <SortHeader label="SLA" field="aging" sort={sort} order={order} onSort={onSort} />
                    <SortHeader label="Waiting since" field="updated_at" sort={sort} order={order} onSort={onSort} />
                    <SortHeader label="Blocked on" field="blocked_on" sort={sort} order={order} onSort={onSort} />
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const canOpen = !!r.user_id;
                    const open = () => { if (canOpen) navigate(`/console/${r.user_id}`); };
                    return (
                    <tr
                      key={r.user_id || r.agreement_id}
                      data-testid={`queue-row-${r.user_id}`}
                      onClick={open}
                      onKeyDown={canOpen ? (e) => {
                        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); }
                      } : undefined}
                      role={canOpen ? "button" : undefined}
                      tabIndex={canOpen ? 0 : undefined}
                      aria-label={canOpen ? `Review ${r.full_name || "participant"}` : undefined}
                      className={`border-t border-slate-100 hover:bg-slate-50 ${
                        r.breach ? "bg-red-50/60" : ""
                      } ${canOpen ? "cursor-pointer" : ""}`}
                    >
                      <td
                        className="p-3 w-8"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        {canOpen && (
                          <input
                            type="checkbox"
                            checked={selected.has(r.user_id)}
                            onChange={() => toggleRow(r.user_id)}
                            data-testid={`queue-select-${r.user_id}`}
                            aria-label={`Select ${r.full_name || "participant"}`}
                            className="cursor-pointer"
                          />
                        )}
                      </td>
                      <td className="p-3">
                        <TierChip tier={r.priority_tier} />
                      </td>
                      <td className="p-3 text-slate-900">
                        <div className="font-medium">{r.full_name || "—"}</div>
                        <div className="text-xs text-slate-500">{r.email || ""}</div>
                      </td>
                      <td className="p-3 font-mono text-xs text-slate-900">{r.vc_number || "—"}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded border text-xs ${statusPill(r.agreement_status)}`}>
                          {r.agreement_status || "—"}
                        </span>
                      </td>
                      <td className="p-3 text-slate-900 tabular-nums">
                        {r.amount_elected != null ? formatCurrency(r.amount_elected) : "—"}
                      </td>
                      <td className="p-3">
                        <AgingBadge aging={r.aging} dueBy={r.due_by} />
                      </td>
                      <td className="p-3 text-xs text-slate-600"
                          title={fmtDateTime(r.waiting_since || r.updated_at)}>
                        {waitingSince(r.waiting_since || r.updated_at)}
                      </td>
                      <td className="p-3 text-xs text-slate-600">{blockedOn(r.agreement_status)}</td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); open(); }}
                          className="border-slate-300 text-slate-700"
                          data-testid={`queue-open-${r.user_id}`}
                        >
                          Review <ChevronRight size={14} />
                        </Button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* PAGINATION */}
      {!loading && !error && total > 0 && (
        <div className="flex items-center justify-between mt-4" data-testid="queue-pagination">
          <p className="text-xs text-slate-500">
            Showing {pageStart}–{pageEnd} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!canPrev}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              className="border-slate-300 text-slate-700"
              data-testid="queue-prev"
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!canNext}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              className="border-slate-300 text-slate-700"
              data-testid="queue-next"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueueTable;
